import api from '@disco/api/server'
import {
  loadNotifications,
  processWebsocketNotification,
  trackUpdateNotification,
} from '@disco/state-management/actions/NotificationActions'
import { getUser } from '@disco/state-management/selectors/user'
import { websockets } from '@disco/utils/websockets'
import { useCallback, useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import type { Location } from 'react-router-dom'
import type { JsonNotification, PongMessage } from 'types/notification'
import type { User } from 'types/user'
import type { State } from '@disco/state-management/reducers/index'

type Props = {
  location?: Location
}

const _onVisibilityChange = () => {
  // only allow reconnect if the tab is focused
  // if the user is not actively using the app, we don't need to reconnect
  // until they come back to the app (which is handled by the updateConnection() below)
  websockets.setAllowReconnect(document.visibilityState === 'visible')

  if (document.visibilityState === 'visible') {
    websockets.updateConnection(true)
  }
}

// Component takes care of websocket handling, you probably need only one
// component for entire app
const NotificationManager = ({ location }: Props): any => {
  const user: User = useSelector(state => getUser(state))
  const online: boolean = useSelector((state: State) => state.app.online)
  const [isOnline, setIsOnline] = useState(online)
  const dispatch = useDispatch() as any
  const processNotification = useCallback(
    (notification: JsonNotification | PongMessage) =>
      dispatch(
        processWebsocketNotification({
          location,
          notification,
        }),
      ),
    [dispatch, location],
  )
  const processMissedNotifications = useCallback(
    (notifications: JsonNotification[]) => {
      // we can process track updates effectively
      const trackUpdates = notifications.filter(n => n.type === 'track_update')
      if (trackUpdates.length > 0)
        dispatch(trackUpdateNotification(trackUpdates))
      // the rest can be optimized, low hanging fruit are playlist related
      // notifications
      const notOptimizedNotifications = notifications.filter(
        n => n.type !== 'track_update',
      )
      notOptimizedNotifications.forEach(n => processNotification(n))
    },
    [dispatch, processNotification],
  )
  useEffect(
    function setWebsocketHandlers() {
      websockets.setHandlers({
        notificationHandler: processNotification,
        bulkNotificationsHandler: processMissedNotifications,
      })
    },
    [processMissedNotifications, processNotification],
  )
  useEffect(() => {
    const pullData = () => {
      return api.getNotifications().then(response => {
        return dispatch(
          loadNotifications(
            (response.results || []).filter(
              (n: JsonNotification) => n.type !== 'receive_share_password',
            ),
          ),
        )
      })
    }

    const getCsrfToken = async () => {
      if (!user?.loggedIn) await api.getCSRFToken()
    }

    if (user?.loggedIn) {
      pullData()
    } else {
      // we should be able to open connection on inbox pages
      getCsrfToken()
    }

    websockets.openConnection()

    const updateConnection = () => {
      websockets.updateConnection(false)
    }

    window.addEventListener('focus', updateConnection)
    window.addEventListener('online', updateConnection)
    window.addEventListener('visibilitychange', _onVisibilityChange)

    return () => {
      window.removeEventListener('focus', updateConnection)
      window.removeEventListener('online', updateConnection)
      window.removeEventListener('visibilitychange', _onVisibilityChange)
      websockets.closeConnection()
    }
  }, [dispatch, user?.loggedIn])
  useEffect(() => {
    if (online !== isOnline) {
      if (online) {
        websockets.openConnection()
      } else {
        websockets.closeConnection()
      }

      setIsOnline(online)
    }
  }, [online, isOnline])
  return null
}

export default NotificationManager
